import Axios from "axios";
import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Row,
  Tooltip,
  OverlayTrigger,
} from "react-bootstrap";
import Container from "react-bootstrap/Container";
import Modal from "react-bootstrap/Modal";
import { FaEye } from "react-icons/fa";
import { Link } from "react-router-dom";
import * as XLSX from "xlsx";
import { useTranslation } from "react-i18next";
import avatar3 from "../assets/images/user/avatar-3.jpg";
import useStoredPermissions from "./useStoredPermissions";
import { DataTablePagination } from "./utils/dataTablePagination";
import FilePicker from "./common/filePicker.jsx";
import { DropDownDepartments } from "./departments/dropDownDepartments";
import { DropDownRols } from "./common/dropDownRol";
import { DropDownClients } from "./clients/dropDownClients";
import ClipLoader from "react-spinners/ClipLoader";
import { SideBar } from "./sideBar";
import { NavBar } from "./navBar";
import {
  getApiResponseMessage,
  getApiErrorMessage,
} from "../utils/apiMessageHelper";
// Contexts
import { useNotification } from "../contexts/NotificationContext";
import { useAuth } from "../contexts/AuthContext";
// Utils
import formatDate from "../utils/formatDate";
// Enums
import { UserStatus } from "../enums/userEnums.js";
import { createMultipleUsers, getRolesData } from "../api/apiClient.js";
import useClientsStore from "../stores/useClientsStore";

export const MainUsers = () => {
  const { t } = useTranslation();
  const permissions = useStoredPermissions() || [];
  const { clientId } = useAuth();
  const { notify } = useNotification();
  const cachedClients = useClientsStore((state) => state.clients);

  // Show sidebar if user has clientId (both Super Admin and Client Admin)
  const hasPermissions = clientId !== null && clientId !== undefined;

  // Check if user is Super Admin (clientId === 1)
  const isSuperAdmin = clientId === 1;

  //> States
  const [data, setData] = useState([]);
  const [userRows, setUserRows] = useState([]);
  const [departmentRows, setDepartmentRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [currentUser, setCurrentUser] = useState([]);
  const [newUser, setNewUser] = useState([]);
  const [userValidated, setUserValidated] = useState(false);
  const [isUserEditMode, setIsUserEditMode] = useState(false);
  const [userActiveSwitch, setUserActiveSwitch] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Selected client for Super Admin (default to their own clientId for Client Admin)
  const [selectedClientId, setSelectedClientId] = useState(
    isSuperAdmin ? null : clientId,
  );

  //> Modals
  const [showNewUser, setShowNewUser] = useState(false);
  const [showCurrentUser, setShowCurrentUser] = useState(false);
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = useState(false);

  const handleShowNewUser = () => {
    if (!selectedClientId) {
      notify(t("users.selectClientBeforeAdd"), { type: "warning" });
      return;
    }
    if (departmentRows.length === 0) {
      notify(t("users.createDepartmentBeforeAdd"), { type: "warning" });
      return;
    }
    setShowNewUser(true);
  };
  const handleCloseNewUser = () => {
    setUserValidated(false);
    setShowNewUser(false);
  };

  const handleShowCurrentUser = () => setShowCurrentUser(true);
  const handleCloseCurrentUser = () => {
    setIsUserEditMode(false);
    setShowCurrentUser(false);
  };

  const handleShowDeleteUserConfirm = () => setShowDeleteUserConfirm(true);
  const handleCloseDeleteUserConfirm = () => setShowDeleteUserConfirm(false);

  const toggleUserActiveHandler = () => {
    setUserActiveSwitch(!userActiveSwitch);
  };

  //> Columns
  const userColumns = [
    {
      Header: " ",
      columns: [
        {
          Header: "",
          accessor: "idw",
          Cell: (props) => {
            return (
              <img
                className="rounded-circle table-profile-icon"
                src={avatar3}
                alt="activity-user"
              ></img>
            );
          },
        },
        { Header: "ID", accessor: "idUser" },
        { Header: t("users.tableHeaderName"), accessor: "firstName" },
        { Header: t("users.tableHeaderLastName"), accessor: "lastName" },
        { Header: t("users.tableHeaderEmail"), accessor: "email" },
        { Header: t("users.tableHeaderPhone"), accessor: "phoneNumber" },
        { Header: t("users.tableHeaderDNI"), accessor: "dni" },
        {
          Header: t("users.tableHeaderDetail"),
          accessor: "detalle",
          Cell: (props) => {
            return (
              <FaEye
                className="fa-table-icon"
                onClick={() => viewCurrentUser(props.cell.row.original)}
              />
            );
          },
        },
      ],
    },
  ];

  //> API Calls
  async function getUsers() {
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/User/list/${selectedClientId}`,
      );

      if (res.data.length === 0) {
        setData((prev) => ({
          ...prev,
          message: t("users.noUsersMessage"),
        }));
      }

      setUserRows(res.data.responseObject);
    } catch (e) {
      setData((prev) => ({
        ...prev,
        message: e.message,
      }));
    }
  }

  async function getDepartments(clientIdParam) {
    const targetClientId = clientIdParam ?? selectedClientId;
    try {
      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/Department/list/${targetClientId}`,
      );

      if (res.data.length === 0) {
        setData((prev) => ({
          ...prev,
          message: t("departments.noDepartmentsMessage"),
        }));
      }

      const departments = res.data.responseObject || [];
      // Solo actualizar el state cuando se carga para el cliente seleccionado actualmente
      if (!clientIdParam || clientIdParam === selectedClientId) {
        setDepartmentRows(departments);
      }
      return departments;
    } catch (e) {
      setData((prev) => ({
        ...prev,
        message: e.message,
      }));
      return [];
    }
  }

  async function getRoles() {
    try {
      const type = selectedClientId === 1 ? 0 : 1;

      const res = await Axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/User/role/list/${type}`,
      );
      const tempArray = res.data.responseObject.map((item) => {
        item.label = item.name;
        item.value = item.id;
        return item;
      });

      setRoles(tempArray);
    } catch (e) {
      console.error(e);
    }
  }

  async function deleteUser() {
    try {
      const res = await Axios.delete(
        `${process.env.REACT_APP_API_URL}/api/v1/User/${currentUser.idUser}`,
      );
      if (res.status === 200) {
        const message =
          getApiResponseMessage(res) || t("users.userDeletedSuccess");
        notify(message, { type: "success" });
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    } finally {
      setShowCurrentUser(false);
      getUsers();
      setCurrentUser(null);
      handleCloseDeleteUserConfirm();
    }
  }

  async function resetPassword(id) {
    try {
      const res = await Axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/Auth/ResetPassword`,
        id,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
      if (res.status === 200) {
        const message =
          getApiResponseMessage(res) || t("users.resetPasswordSuccess");
        notify(message, { type: "success" });
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    }
  }

  //> Handlers
  const handleNewUserSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      setUserValidated(true);
    } else {
      setUserValidated(false);

      try {
        newUser.status = userActiveSwitch ? 1 : 0;

        // Asegurar que campos opcionales tengan valores válidos para el backend
        const userToSend = {
          ...newUser,
          dni: newUser.dni || "",
          phoneNumber: newUser.phoneNumber || "",
        };

        const res = await Axios.post(
          `${process.env.REACT_APP_API_URL}/api/v1/User/${selectedClientId}`,
          userToSend,
        );
        if (res.status === 200) {
          const message =
            getApiResponseMessage(res) || t("users.userAddedSuccess");
          notify(message, { type: "success" });
        }
      } catch (e) {
        const errorMessage = getApiErrorMessage(e);
        notify(errorMessage, { type: "error" });
      } finally {
        setShowNewUser(false);
        getUsers();
        setNewUser(null);
      }
    }
  };

  const handleEditUserSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;

    if (form.checkValidity() === false) {
      setUserValidated(true);
    } else {
      setUserValidated(false);

      try {
        const payload = { ...currentUser, status: userActiveSwitch ? 1 : 0 };
        const res = await Axios.put(
          `${process.env.REACT_APP_API_URL}/api/v1/User/${currentUser.idUser}`,
          payload,
        );

        if (res.status === 200) {
          const message =
            getApiResponseMessage(res) || t("users.userModifiedSuccess");
          notify(message, { type: "success" });
        }
      } catch (e) {
        const errorMessage = getApiErrorMessage(e);
        notify(errorMessage, { type: "error" });
      } finally {
        getUsers();
        setCurrentUser(null);
        setUserActiveSwitch(true);
        handleCloseCurrentUser();
      }
    }
  };

  const handleNewUserChange = (object) => {
    const { name, value } = object.target;
    setNewUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCurrentUserChange = (object) => {
    const { name, value } = object.target;
    setCurrentUser((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  function handleUserDropdownChange(name, item, type) {
    if (type === "new") {
      setNewUser((prev) => ({
        ...prev,
        [name]: item.value,
      }));
    } else if (type === "edit") {
      setCurrentUser((prev) => ({
        ...prev,
        [name]: item.value,
      }));
    }
  }

  const handleUserEditMode = () => {
    if (!isUserEditMode) {
      setUserActiveSwitch(currentUser.status === 1);
    }
    setIsUserEditMode(!isUserEditMode);
  };

  const viewCurrentUser = (object) => {
    setCurrentUser(object);
    handleShowCurrentUser();
  };

  const handleMassiveLoad = async (file) => {
    setIsUploading(true);
    try {
      // Validar Client Admin: selectedClientId debe ser el clientId del usuario
      if (!isSuperAdmin && selectedClientId !== clientId) {
        notify(t("users.selectClientError"), {
          type: "error",
        });
        return;
      }

      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const usersDataJSON = XLSX.utils.sheet_to_json(worksheet, {
        defval: "",
        raw: false,
        cellText: true,
      });

      // Validar Client Admin: no puede cargar usuarios de múltiples clientes
      if (!isSuperAdmin) {
        const uniqueClientNames = [
          ...new Set(
            usersDataJSON
              .map((row) => row["Cliente"]?.toString().trim().toLowerCase())
              .filter(Boolean),
          ),
        ];
        if (uniqueClientNames.length > 1) {
          notify(t("users.multipleClientsLoadError"), { type: "error" });
          return;
        }
      }

      const rolesData = await getRolesData();
      const errors = [];

      // Construir mapa de departamentos por cliente
      const departmentsByClient = new Map();

      if (isSuperAdmin) {
        // Super Admin: extraer clientes únicos del archivo, resolver IDs y cargar departamentos por cliente
        const clientsData = cachedClients;
        const uniqueClientNames = [
          ...new Set(
            usersDataJSON
              .map((row) => row["Cliente"]?.toString().trim().toLowerCase())
              .filter(Boolean),
          ),
        ];

        for (const name of uniqueClientNames) {
          const client = clientsData.find((c) => c.name.toLowerCase() === name);
          if (client && !departmentsByClient.has(client.id)) {
            const deps = await getDepartments(client.id);
            departmentsByClient.set(client.id, deps);
          }
        }
      } else {
        // Client Admin: cargar departamentos solo para su propio cliente
        const deps = await getDepartments(clientId);
        departmentsByClient.set(clientId, deps);
      }

      const fixed = usersDataJSON.map((row, index) => {
        const departmentName = row["Departamento"]?.toLowerCase();
        const roleName = row["Rol"]?.toLowerCase();

        let idClient;
        if (isSuperAdmin) {
          const clientName = row["Cliente"]?.toString().trim().toLowerCase();
          idClient = cachedClients.find(
            (c) => c.name.toLowerCase() === clientName,
          )?.id;
        } else {
          idClient = clientId;
        }

        // Buscar departamento dentro del contexto del cliente correspondiente
        const clientDepartments = idClient
          ? departmentsByClient.get(idClient) || []
          : [];
        const idDepartment = clientDepartments.find(
          (department) => department.name.toLowerCase() === departmentName,
        )?.idDepartment;
        const idRol = rolesData.find(
          (role) => role.name.toLowerCase() === roleName,
        )?.id;

        const rawStatus = row["Estado"]?.toString().trim().toLowerCase();
        const validActiveValues = ["activo", "active"];
        const validInactiveValues = ["inactivo", "inactive"];
        const isValidStatus =
          validActiveValues.includes(rawStatus) ||
          validInactiveValues.includes(rawStatus);

        const rowErrors = [];
        if (isSuperAdmin && !idClient) rowErrors.push(`Cliente no encontrado`);
        if (!idDepartment) rowErrors.push(`Departamento no encontrado`);
        if (!idRol) rowErrors.push(`Rol no encontrado`);
        if (!isValidStatus)
          rowErrors.push(`Estado inválido: "${row["Estado"]}" (valores válidos: activo, inactivo)`);

        if (rowErrors.length > 0) {
          errors.push(`Fila ${index + 2}: ${rowErrors.join(", ")}`);
        }

        return {
          email: row["Correo"]?.toString().trim() || "",
          firstName: row["Nombre"]?.toString().trim() || "",
          lastName: row["Apellido"]?.toString().trim() || "",
          phoneNumber: row["Numero de telefono"]?.toString().trim() || "",
          dni: row["Cedula"]?.toString().trim() || "",
          birthDate: row["Fecha de Nacimiento"]
            ? new Date(row["Fecha de Nacimiento"]).toISOString()
            : null,
          status: validActiveValues.includes(rawStatus)
            ? UserStatus.Active
            : UserStatus.Inactive,
          idDepartment: idDepartment,
          idRol: idRol,
          idClient: idClient,
        };
      });

      if (errors.length > 0) {
        notify(`Errores en los datos:\n${errors.join("\n")}`, {
          type: "error",
        });
        return;
      }
      const response = await createMultipleUsers(fixed);

      if (response.status === 202) {
        const message =
          getApiResponseMessage(response) || t("users.usersInsertedSuccess");
        notify(message, { type: "success" });
        getUsers();
      } else {
        const errorMessage = getApiErrorMessage({ response });
        notify(errorMessage, { type: "error" });
      }
    } finally {
      setIsUploading(false);
    }
  };

  //> Helper Functions
  const getDepartmentName = (departmentId) => {
    const department = departmentRows.find(
      (dept) => dept.idDepartment === departmentId,
    );
    return department ? department.name : t("common.notAssigned");
  };

  const getRoleName = (roleId) => {
    const role = roles.find((role) => role.id === roleId);
    return role ? role.name : t("common.notAssigned");
  };

  const filteredUsers = userRows.filter((user) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      user.firstName?.toLowerCase().includes(term) ||
      user.lastName?.toLowerCase().includes(term) ||
      user.email?.toLowerCase().includes(term)
    );
  });

  const handleClientChange = (selected) => {
    if (selected) {
      // The dropdown now passes the full client object with idClient
      const newClientId = selected.idClient;
      // Allow setting to null when empty option is selected
      setSelectedClientId(newClientId);
      setSearchTerm("");
      // Clear users when empty option is selected
      if (newClientId === null) {
        setUserRows([]);
        setDepartmentRows([]);
      }
    }
  };

  //> Lifecycle
  useEffect(() => {
    if (selectedClientId) {
      setIsLoading(true);
      Promise.all([getUsers(), getDepartments(), getRoles()]).finally(() =>
        setIsLoading(false),
      );
    }
  }, [selectedClientId]);

  return (
    <React.Fragment>
      {hasPermissions && <SideBar />}
      <Container>
        <NavBar />
        <Container>
          <Row>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <Card.Title as="h5">{t("users.pageTitle")}</Card.Title>
                  {isSuperAdmin && (
                    <div style={{ minWidth: "250px" }}>
                      <DropDownClients
                        value={selectedClientId}
                        onChange={handleClientChange}
                        className="form-control"
                      />
                    </div>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <div id="flexDiv">
                  <Button
                    id="btnLarge"
                    variant="primary"
                    onClick={handleShowNewUser}
                    disabled={!permissions.includes("Add/Update User")}
                  >
                    {t("users.addButton")}
                  </Button>
                  <FilePicker
                    label={
                      isUploading ? (
                        <>
                          <ClipLoader size={14} color={"#fff"} loading={true} />{" "}
                          {t("common.processing")}
                        </>
                      ) : (
                        t("users.massiveLoadButton")
                      )
                    }
                    onFileSelected={handleMassiveLoad}
                    id={"btnLarge"}
                    variant={"secondary"}
                    disabled={
                      !permissions.includes("Add/Update User") || isUploading
                    }
                    accept={".xlsx,.xls"}
                  />
                </div>
                {userRows.length > 0 && (
                  <Form.Control
                    type="text"
                    placeholder={t("users.searchPlaceholder")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="mb-3"
                  />
                )}
                <Col xs={12}>
                  {isLoading ? (
                    <div className="load-icon">
                      <ClipLoader
                        size={50}
                        color={"#0b5ed7"}
                        loading={isLoading}
                      />
                    </div>
                  ) : userRows.length === 0 ? (
                    <div className="no-data">{data?.message}</div>
                  ) : (
                    <DataTablePagination
                      columns={userColumns}
                      data={filteredUsers}
                      onRowClick={(selectedRow) => setCurrentUser(selectedRow)}
                      hiddenColumns={["idUser"]}
                    />
                  )}
                </Col>
              </Card.Body>
            </Card>
          </Row>
        </Container>
      </Container>

      {/* Modal: Create New User */}
      <Modal show={showNewUser} onHide={handleCloseNewUser}>
        <Form
          className="addForm"
          onSubmit={handleNewUserSubmit}
          noValidate
          validated={userValidated}
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("users.createUserModalTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="justify-content-md-center">
              {/* First Name */}
              <InputGroup className="mb-3">
                <InputGroup.Text>{t("users.firstNameLabel")}</InputGroup.Text>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip id="firstNameTooltip">
                      {t("users.firstNameTooltip")}
                    </Tooltip>
                  }
                >
                  <Form.Control
                    required
                    minLength={3}
                    type="text"
                    name="firstName"
                    value={newUser?.firstName || ""}
                    placeholder={t("users.firstNameLabel")}
                    onChange={handleNewUserChange}
                    isInvalid={userValidated && newUser?.firstName?.length < 3}
                  />
                </OverlayTrigger>
              </InputGroup>

              {/* Last Name */}
              <InputGroup className="mb-3">
                <InputGroup.Text>{t("users.lastNameLabel")}</InputGroup.Text>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip id="lastNameTooltip">
                      {t("users.lastNameTooltip")}
                    </Tooltip>
                  }
                >
                  <Form.Control
                    required
                    minLength={3}
                    type="text"
                    name="lastName"
                    value={newUser?.lastName || ""}
                    placeholder={t("users.lastNameLabel")}
                    onChange={handleNewUserChange}
                    isInvalid={userValidated && newUser?.lastName?.length < 3}
                  />
                </OverlayTrigger>
              </InputGroup>

              {/* Email */}
              <InputGroup className="mb-3">
                <InputGroup.Text>{t("users.emailLabel")}</InputGroup.Text>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip id="emailTooltip">
                      {t("users.emailTooltip")}
                    </Tooltip>
                  }
                >
                  <Form.Control
                    required
                    type="email"
                    name="email"
                    value={newUser?.email || ""}
                    placeholder={t("users.emailLabel")}
                    onChange={handleNewUserChange}
                    isInvalid={
                      userValidated && !/\S+@\S+\.\S+/.test(newUser?.email)
                    }
                  />
                </OverlayTrigger>
              </InputGroup>

              {/* Phone Number */}
              <InputGroup className="mb-3">
                <InputGroup.Text>{t("users.phoneLabel")}</InputGroup.Text>
                <OverlayTrigger
                  placement="right"
                  show={
                    newUser?.phoneNumber &&
                    newUser.phoneNumber.length > 0 &&
                    newUser.phoneNumber.length < 5
                  }
                  overlay={
                    <Tooltip id="phoneTooltip">
                      {t("users.phoneTooltip")}
                    </Tooltip>
                  }
                >
                  <Form.Control
                    minLength={
                      newUser?.phoneNumber && newUser.phoneNumber.length > 0
                        ? 5
                        : undefined
                    }
                    type="text"
                    name="phoneNumber"
                    value={newUser?.phoneNumber || ""}
                    placeholder={t("users.phoneOptional")}
                    onChange={handleNewUserChange}
                    isInvalid={
                      userValidated &&
                      newUser?.phoneNumber &&
                      newUser.phoneNumber.length > 0 &&
                      newUser.phoneNumber.length < 5
                    }
                  />
                </OverlayTrigger>
              </InputGroup>

              {/* DNI */}
              <InputGroup className="mb-3">
                <InputGroup.Text>{t("users.dniLabel")}</InputGroup.Text>
                <OverlayTrigger
                  placement="right"
                  show={
                    newUser?.dni &&
                    newUser.dni.length > 0 &&
                    newUser.dni.length < 5
                  }
                  overlay={
                    <Tooltip id="dniTooltip">{t("users.dniTooltip")}</Tooltip>
                  }
                >
                  <Form.Control
                    minLength={
                      newUser?.dni && newUser.dni.length > 0 ? 5 : undefined
                    }
                    type="text"
                    name="dni"
                    value={newUser?.dni || ""}
                    placeholder={t("users.dniOptional")}
                    onChange={handleNewUserChange}
                    isInvalid={
                      userValidated &&
                      newUser?.dni &&
                      newUser.dni.length > 0 &&
                      newUser.dni.length < 5
                    }
                  />
                </OverlayTrigger>
              </InputGroup>

              {/* Birth Date */}
              <InputGroup className="mb-3">
                <InputGroup.Text>{t("users.birthDateLabel")}</InputGroup.Text>
                <OverlayTrigger
                  placement="right"
                  overlay={
                    <Tooltip id="birthDateTooltip">
                      {t("users.birthDateTooltip")}
                    </Tooltip>
                  }
                >
                  <Form.Control
                    required
                    type="date"
                    name="birthDate"
                    value={newUser?.birthDate || ""}
                    placeholder="Fecha Nacimiento"
                    onChange={handleNewUserChange}
                    isInvalid={userValidated && !newUser?.birthDate}
                  />
                </OverlayTrigger>
              </InputGroup>

              {/* Department */}
              <InputGroup className="mb-3">
                <InputGroup.Text>{t("users.departmentLabel")}</InputGroup.Text>
                <DropDownDepartments
                  required
                  className="form-control"
                  onChange={(item) =>
                    handleUserDropdownChange("idDepartment", item, "new")
                  }
                  value={0}
                  idClient={selectedClientId}
                />
              </InputGroup>

              {/* Rol */}
              <InputGroup className="mb-3">
                <InputGroup.Text>{t("users.rolLabel")}</InputGroup.Text>
                <DropDownRols
                  required
                  className="form-control"
                  onChange={(item) =>
                    handleUserDropdownChange("idRol", item, "new")
                  }
                  value={newUser?.idRol || ""}
                  idClient={selectedClientId}
                />
              </InputGroup>

              {/* Status */}
              <Form.Group>
                <Form.Label>{t("users.activeLabel")}</Form.Label>
                <div className="switch d-inline m-r-10">
                  <Form.Control
                    type="checkbox"
                    id="checked-default"
                    defaultChecked={userActiveSwitch}
                    onChange={toggleUserActiveHandler}
                  />
                  <Form.Label htmlFor="checked-default" className="cr" />
                </div>
              </Form.Group>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseNewUser}>
              {t("common.close")}
            </Button>
            <Button variant="primary" type="submit">
              {t("common.save")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: View/Edit Current User */}
      <Modal
        className="modal-lg"
        show={showCurrentUser}
        onHide={handleCloseCurrentUser}
      >
        <Form
          className="addForm"
          onSubmit={handleEditUserSubmit}
          noValidate
          validated={userValidated}
        >
          <Modal.Header closeButton>
            <Modal.Title>{t("users.viewUserModalTitle")}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="justify-content-md-center">
              <Card>
                <Card.Body className="p-0">
                  <div>
                    <div className="text-center project-main">
                      <img
                        className="img-fluid rounded-circle"
                        src={avatar3}
                        alt="dashboard-user"
                      />
                      {/* Info View */}
                      <Row
                        style={{ display: !isUserEditMode ? "flex" : "none" }}
                      >
                        <Col xs={6}>
                          {/* First Name */}
                          <div className="row m-t-30">
                            <div className="col-4">
                              <span>{t("users.userNameLabel")}</span>
                            </div>
                            <div className="col-8">
                              <h6>{currentUser?.firstName}</h6>
                            </div>
                          </div>
                          {/* Last Name */}
                          <div className="row m-t-30">
                            <div className="col-4">
                              <span>{t("users.userLastNameLabel")}</span>
                            </div>
                            <div className="col-8">
                              <h6>{currentUser?.lastName}</h6>
                            </div>
                          </div>
                          {/* Email */}
                          <div className="row m-t-30">
                            <div className="col-4">
                              <span>{t("users.userEmailLabel")}</span>
                            </div>
                            <div className="col-8">
                              <h6>{currentUser?.email}</h6>
                            </div>
                          </div>
                          {/* Phone */}
                          <div className="row m-t-30">
                            <div className="col-4">
                              <span>{t("users.userPhoneLabel")}</span>
                            </div>
                            <div className="col-8">
                              <h6>{currentUser?.phoneNumber}</h6>
                            </div>
                          </div>
                          {/* DNI */}
                          <div className="row m-t-30">
                            <div className="col-4">
                              <span>{t("users.userDNILabel")}</span>
                            </div>
                            <div className="col-8">
                              <h6>{currentUser?.dni}</h6>
                            </div>
                          </div>
                        </Col>
                        <Col xs={6}>
                          {/* Birth Date */}
                          <div className="row m-t-30">
                            <div className="col-4">
                              <span>{t("users.userBirthDateLabel")}</span>
                            </div>
                            <div className="col-8">
                              <h6>
                                {currentUser?.birthDate
                                  ? formatDate(currentUser?.birthDate)
                                  : ""}
                              </h6>
                            </div>
                          </div>
                          {/* Department */}
                          <div className="row m-t-30">
                            <div className="col-4">
                              <span>{t("users.userDepartmentLabel")}</span>
                            </div>
                            <div className="col-8">
                              <h6>
                                {getDepartmentName(currentUser?.idDepartment)}
                              </h6>
                            </div>
                          </div>
                          {/* Rol */}
                          <div className="row m-t-30">
                            <div className="col-4">
                              <span>{t("users.userRolLabel")}</span>
                            </div>
                            <div className="col-8">
                              <h6>{getRoleName(currentUser?.idRol)}</h6>
                            </div>
                          </div>
                          {/* Status */}
                          <div className="row m-t-30">
                            <div className="col-4">
                              <span>{t("users.userStatusLabel")}</span>
                            </div>
                            <div className="col-8">
                              {currentUser?.status === 1 ? (
                                <a className="label theme-bg text-white f-12 status-icon">
                                  {t("users.userActiveStatus")}
                                </a>
                              ) : (
                                <a className="label theme-bg2 text-white f-12 status-icon">
                                  {t("users.userInactiveStatus")}
                                </a>
                              )}
                            </div>
                          </div>
                        </Col>
                      </Row>
                      {/* Edit View */}
                      <Row
                        style={{ display: isUserEditMode ? "flex" : "none" }}
                      >
                        <Col xs={6}>
                          {/* First Name */}
                          <InputGroup className="mb-3">
                            <InputGroup.Text>
                              {t("users.firstNameLabel")}
                            </InputGroup.Text>
                            <Form.Control
                              required
                              minLength={3}
                              autoFocus
                              type="text"
                              name="firstName"
                              value={currentUser?.firstName || ""}
                              placeholder={t("users.firstNameLabel")}
                              onChange={handleCurrentUserChange}
                            />
                          </InputGroup>
                          {/* Last Name */}
                          <InputGroup className="mb-3">
                            <InputGroup.Text>
                              {t("users.lastNameLabel")}
                            </InputGroup.Text>
                            <Form.Control
                              required
                              minLength={3}
                              autoFocus
                              type="text"
                              name="lastName"
                              value={currentUser?.lastName || ""}
                              placeholder={t("users.lastNameLabel")}
                              onChange={handleCurrentUserChange}
                            />
                          </InputGroup>
                          {/* Email */}
                          <InputGroup className="mb-3">
                            <InputGroup.Text>
                              {t("users.emailLabel")}
                            </InputGroup.Text>
                            <Form.Control
                              required
                              minLength={2}
                              autoFocus
                              type="email"
                              name="email"
                              value={currentUser?.email || ""}
                              placeholder={t("users.emailLabel")}
                              onChange={handleCurrentUserChange}
                            />
                          </InputGroup>
                          {/* Phone Number */}
                          <InputGroup className="mb-3">
                            <InputGroup.Text>
                              {t("users.phoneLabel")}
                            </InputGroup.Text>
                            <Form.Control
                              minLength={
                                currentUser?.phoneNumber &&
                                currentUser.phoneNumber.length > 0
                                  ? 5
                                  : undefined
                              }
                              autoComplete="off"
                              type="text"
                              name="phoneNumber"
                              value={currentUser?.phoneNumber || ""}
                              placeholder={t("users.phoneOptional")}
                              onChange={handleCurrentUserChange}
                            />
                          </InputGroup>
                          {/* DNI */}
                          <InputGroup className="mb-3">
                            <InputGroup.Text>
                              {t("users.dniLabel")}
                            </InputGroup.Text>
                            <Form.Control
                              minLength={
                                currentUser?.dni && currentUser.dni.length > 0
                                  ? 5
                                  : undefined
                              }
                              autoComplete="off"
                              type="text"
                              name="dni"
                              value={currentUser?.dni || ""}
                              placeholder={t("users.dniOptional")}
                              onChange={handleCurrentUserChange}
                            />
                          </InputGroup>
                        </Col>
                        <Col xs={6}>
                          {/* Birth Date */}
                          <InputGroup className="mb-3">
                            <InputGroup.Text>
                              {t("users.birthDateLabel")}
                            </InputGroup.Text>
                            <Form.Control
                              required
                              minLength={5}
                              autoFocus
                              autoComplete="off"
                              type="date"
                              name="birthDate"
                              value={
                                currentUser?.birthDate
                                  ? currentUser.birthDate.split("T")[0]
                                  : ""
                              }
                              placeholder="Fecha Nacimiento"
                              onChange={handleCurrentUserChange}
                            />
                          </InputGroup>
                          {/* Department */}
                          <InputGroup className="mb-3">
                            <DropDownDepartments
                              required
                              className="form-control"
                              onChange={(item) =>
                                handleUserDropdownChange(
                                  "idDepartment",
                                  item,
                                  "edit",
                                )
                              }
                              value={currentUser?.idDepartment}
                              idClient={selectedClientId}
                            />
                          </InputGroup>
                          {/* Rol */}
                          <InputGroup className="mb-3">
                            <DropDownRols
                              required
                              className="form-control"
                              onChange={(item) =>
                                handleUserDropdownChange("idRol", item, "edit")
                              }
                              value={currentUser?.idRol}
                              idClient={selectedClientId}
                            />
                          </InputGroup>
                          {/* Status */}
                          <Form.Group>
                            <Form.Label>{t("users.activeLabel")}</Form.Label>
                            <div className="switch d-inline m-r-10">
                              <Form.Control
                                type="checkbox"
                                id="checked-default"
                                defaultChecked={
                                  currentUser?.status === 1 ? true : false
                                }
                                onChange={toggleUserActiveHandler}
                              />
                              <Form.Label
                                htmlFor="checked-default"
                                className="cr"
                              />
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>
                      <div
                        className="row m-t-30"
                        style={{
                          visibility:
                            currentUser?.idUser !== null ? "visible" : "hidden",
                        }}
                      >
                        <div className="col-4 p-r-0">
                          {!isUserEditMode && (
                            <Link
                              onClick={handleUserEditMode}
                              className="btn btn-primary  text-uppercase btn-block"
                            >
                              {t("common.edit")}
                            </Link>
                          )}
                          {isUserEditMode && (
                            <Button
                              type="submit"
                              className="btn btn-primary  text-uppercase btn-block"
                            >
                              {t("common.save")}
                            </Button>
                          )}
                        </div>
                        <div className="col-4 p-r-0">
                          <Button
                            onClick={() => resetPassword(currentUser.idUser)}
                            className="btn btn-warning  text-uppercase btn-block"
                          >
                            {t("users.resetPasswordButton")}
                          </Button>
                        </div>
                        <div className="col-4">
                          <Button
                            onClick={handleShowDeleteUserConfirm}
                            className="btn text-uppercase border btn-block btn-outline-danger"
                            disabled={!permissions.includes("Delete User")}
                          >
                            {t("common.delete")}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseCurrentUser}>
              {t("common.close")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Delete User Confirmation */}
      <Modal show={showDeleteUserConfirm} onHide={handleCloseDeleteUserConfirm}>
        <Modal.Header closeButton>
          <Modal.Title>{t("common.confirmDeleteTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("users.deleteUserConfirmMessage")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteUserConfirm}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" onClick={deleteUser}>
            {t("common.delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};
